<template>
  <div
      class="productsContainer tw-fixed tw-top-0 tw-h-full tw-bg-solBlack tw-z-[200] tw-shadow-2xl tw-flex tw-flex-col tw-gap-5 tw-items-center tw-border-r tw-border-borderLight20 tw-rounded-tr-2xl tw-rounded-br-2xl"
      :class="{ visible: visible }"
  >
    <div class="tw-w-full tw-items-center tw-text-center">
      <div class="tw-flex tw-justify-between tw-p-4 tw-border-b tw-border-borderLight20">
        <div>
          <p class="tw-text-left tw-m-0 tw-text-white">
            {{ $t("order") }}:
            <a class="badge tw-bg-solYellow tw-text-black" target="_blank" :href="order.orderStatusUrl">{{
                order.orderIDShort
              }}</a>
          </p>
          <p class="tw-text-start tw-text-gray-400 tw-text-sm tw-mt-2">
            <span class="tw-text-white tw-font-semibold">{{ $t('productsNoteLabel') }}</span> {{ $t('productsNoteText') }}
          </p>
          <p class="tw-text-left tw-m-0 tw-mt-2 tw-text-white">
            {{ $t("status") }}:
            <span
                :class="{
                'tw-badge in-progress': true,
                printed: order.status === 'printed',
                done: order.status === 'submitted',
              }"
            >{{ $t("design_status_" + (order.status || "created")) }}</span
            >
          </p>
        </div>
        <img
            id="collapseProducts"
            :src="getAssetPath('/assets/images/left.png')"
            :alt="$t('collapse')"
            width="24"
            height="24"
            @click="$emit('productsVisible', false)"
            class="tw-bg-gray-700 tw-rounded-full tw-p-2 tw-cursor-pointer hover:tw-bg-gray-600 tw-transition-colors"
        />
      </div>

      <div v-if="copy || importing || assigning" class="tw-p-3">
        <!-- Paste to all Button -->
        <div
            v-if="copy"
            class="tw-bg-solYellow tw-text-textDark tw-font-semibold tw-m-2 tw-cursor-pointer tw-p-2 tw-rounded-xl hover:tw-scale-105"
            @click="pasteToAllAtOnce()"
        >
          {{ $t("productsSidebarPasteDesignAll") }}
        </div>

        <!-- Assign to all Button -->
        <div
            v-if="assigning"
            class="tw-bg-solYellow tw-text-textDark tw-font-semibold tw-m-2 tw-cursor-pointer tw-p-2 tw-rounded-xl hover:tw-scale-105"
            @click="assignToAll()"
        >
          {{ $t('assignProfilesToAll') }}
        </div>

        <!-- Cancel Copy Button -->
        <div
            v-if="!enableSave && (copy || importing || assigning)"
            class="tw-m-2 tw-cursor-pointer tw-bg-gray-700 tw-text-white tw-p-2 tw-rounded-xl tw-border tw-border-gray-600 hover:tw-bg-gray-600 hover:tw-scale-105 tw-transition-all"
            @click="cancelAction"
        >
          {{ $t("cancel") }}
        </div>
      </div>
    </div>
    <div class="tw-p-3" style="overflow-y: auto">
      <template v-if="orderLoading">
        <h3 class="tw-text-white">{{ $t("loading") }}...</h3>
      </template>
      <template v-else-if="jobs?.length">
        <div
            v-for="(job, index) in jobs"
            :key="index"
            :class="{
            'tw-p-2 tw-border-solid tw-border-4 tw-rounded-3xl tw-text-center tw-mb-6 tw-relative': true,
            productImageParent:
              job.type === jobs[selectedJobIndex]?.type &&
              selectedJobIndex !== index &&
              isSameVariant(job),
            'tw-border-transparent': selectedJobIndex !== index,
            'tw-border-solYellow': selectedJobIndex === index,
          }"
            ref="jobs"
        >
          <span
              @click="copyDesignLink(job)"
              class="tw-bg-gray-700 tw-text-white tw-rounded-full tw-p-2 tw-cursor-pointer tw-border-solid tw-border-gray-600 tw-absolute tw-text-center tw-left-[-10px] tw-top-[-15px] tw-w-[20px] tw-h-[20px] hover:tw-bg-gray-600 tw-transition-colors"
          >
            {{ index + 1 }}
          </span>
          <img
              class="tw-border-2 tw-border-solid tw-border-borderLight20 tw-rounded-2xl tw-cursor-pointer"
              :class="{ '!tw-rounded-full sticker-img': job.type === 'sticker' }"
              :src="
              job.design_preview.front ||
              job.design_preview.back ||
              Object.values(job.design_preview)[0]
            "
              :width="job.type === 'sticker' ? 200 : 300"
              @click="changeSelectedProduct(job, index)"
              :style="`max-width: ${
              job.type === 'sticker' ? 200 : 300
            }px; width: 100%;`"
          />

          <div
              v-if="!!job.profile || variables.length > 0"
              class="tw-relative tw-mt-5"
          >
            <!-- Dropdown toggle -->
            <div
                v-if="
                !copy && !assigning && selectedJobIndex === index && !importing
              "
                class="tw-cursor-pointer tw-bg-gray-700 tw-text-white tw-p-2 tw-rounded-xl tw-m-2 tw-border tw-border-gray-600 hover:tw-bg-gray-600 hover:tw-scale-105 tw-transition-all"
                @click="assignProfile(job.profile, job)"
            >
              <span v-if="!job.profile"> {{ $t("assignProfile") }} </span>
              <span v-else>
                {{ job.profile.name }} ({{ job.profile.email }})
              </span>
            </div>

            <!-- Dropdown menu -->
            <div
                v-show="assigning && selectedJobIndex === index"
                class="tw-z-10 tw-w-full tw-rounded-lg tw-shadow-lg tw-bg-bgDarkElevated tw-border tw-border-gray-700"
            >
              <!-- Search input -->
              <div class="tw-flex tw-items-center tw-rounded-t-lg">
                <input
                    v-model="searchTerm"
                    @input="filterProfiles"
                    type="text"
                    :placeholder="$t('search')"
                    class="tw-w-full tw-rounded-t-lg tw-px-4 tw-py-2 tw-bg-gray-800 tw-text-white tw-border-0"
                />
              </div>

              <!-- Profile options -->
              <div class="tw-max-h-60 tw-overflow-auto">
                <div
                    class="tw-px-4 tw-py-2 hover:tw-bg-gray-700 tw-cursor-pointer tw-text-red-400"
                    @click="assignProfile(null, job)"
                >
                  X
                </div>
                <div
                    v-for="variable in filteredVariables"
                    :key="variable"
                    class="tw-px-4 tw-py-2 hover:tw-bg-gray-700 tw-cursor-pointer tw-text-white"
                    @click="assignProfile(variable, job)"
                >
                  {{ variable.name }}
                  <span v-if="variable.email" class="tw-text-gray-400">({{ variable.email }})</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Replace Variables Button -->
          <div
              v-if="
              !enableSave &&
              !copy &&
              jobs?.length > 1 &&
              !importing &&
              !assigning &&
              (job.variablesReplaced === false || job.profile)
            "
              class="tw-m-2 tw-cursor-pointer tw-bg-gray-700 tw-text-white tw-p-2 tw-rounded-xl tw-border tw-border-gray-600 hover:tw-bg-gray-600 hover:tw-scale-105 tw-transition-all"
              :class="{ 'showCopyIntoCurrentButton': selectedJobIndex === index }"
              @click="assignProfile(job.profile, job, false)"
          >
            {{ $t("replaceVariables") }}
          </div>

          <!-- Copy Button -->
          <div
              v-if="
              !enableSave &&
              !copy &&
              jobs?.length > 1 &&
              !importing &&
              !assigning
            "
              class="tw-m-2 tw-cursor-pointer tw-bg-gray-700 tw-text-white tw-p-2 tw-rounded-xl tw-border tw-border-gray-600 hover:tw-bg-gray-600 hover:tw-scale-105 tw-transition-all"
              :class="{ 'showCopyIntoCurrentButton': selectedJobIndex === index }"
              @click="copyDesign"
          >
            {{ $t("productsSidebarCopyDesign") }}
          </div>

          <!-- Import Button -->
          <div
              v-if="importing && selectedJobIndex === index && !assigning"
              class="tw-flex-1 tw-flex tw-justify-center tw-font-sans"
          >
            <input
                class="form-input tw-w-full tw-rounded-full tw-py-2 tw-px-4 tw-border tw-border-solid tw-border-solYellow tw-shadow-sm tw-bg-gray-800 tw-text-white tw-m-2 placeholder:tw-text-gray-400"
                type="text"
                v-model="importURL"
                :placeholder="$t('designID')"
            />
          </div>

          <div
              v-if="!enableSave && !copy && !assigning"
              class="tw-m-2 tw-cursor-pointer tw-bg-gray-700 tw-text-white tw-p-2 tw-rounded-xl tw-border tw-border-gray-600 hover:tw-bg-gray-600 hover:tw-scale-105 tw-transition-all"
              :class="{ 'showCopyIntoCurrentButton': selectedJobIndex === index }"
              @click="importDesign"
          >
            {{ $t("import") }}
          </div>

          <!-- Paste Button -->
          <div
              v-if="copy && selectedJobIndex !== index && isSameVariant(job)"
              class="tw-m-2 tw-cursor-pointer tw-bg-gray-700 tw-text-white tw-p-2 tw-rounded-xl tw-border tw-border-gray-600 hover:tw-bg-gray-600 hover:tw-scale-105 tw-transition-all"
              @click="paste(job, index)"
          >
            {{ $t("productsSidebarPasteDesign") }}
          </div>
        </div>
        <div>&nbsp;</div>
      </template>
      <template v-else>
        <h3 class="tw-text-white">{{ $t("productsSidebarEmptyOrder") }}</h3>
      </template>
    </div>

    <div v-if="!orderLoading" class="tw-w-full tw-items-center tw-text-center tw-mb-3">
      <div class="tw-flex tw-justify-between">
        <div class="progress-container tw-p-5 tw-pt-1 tw-pb-1">
          <div class="progress-label">
            {{ $t("savedDesigns") }} ({{ savedJobs?.length }}/{{
              jobs?.length
            }})
            <span style="float: right"
            >{{ savedJobs?.length === jobs?.length ? "🟢" : "🟡" }}
            </span>
          </div>
          <div class="progress-bar" v-if="jobs?.length < 50">
            <div
                v-for="(job, index) in jobs"
                :key="index"
                :class="{
                done: job.design_ready,
                pending: !job.design_ready,
                selected: index === selectedJobIndex,
              }"
                @click="changeSelectedProduct(job, index, false, false, true)"
                class="progress-segment"
            ></div>
          </div>
          <div
              class="tw-bg-solBlack tw-text-white tw-font-semibold tw-m-2 tw-mt-5 tw-cursor-pointer tw-p-2 tw-rounded-xl tw-border-2 tw-border-solid tw-border-solYellow tw-transition-all"
              :class="{
              'tw-opacity-30': !enableSubmit,
              'hover:tw-bg-solBlackHover hover:tw-scale-105': enableSubmit,
            }"
              @click="submitDesign"
          >
            <span
                v-if="order.status === 'submitted' || order.status === 'printed'"
            >
              {{ $t("designSubmitted") }}
            </span>
            <span v-else>
              {{ $t("submitDesign") }}
            </span>
          </div>
          <PromptModal
              ref="promptModalSubmit"
              :title-text="$t('designSubmittedTitle')"
              :subtitle-text="$t('designSubmittedSubtitle')"
              :button-close-text="$t('designSubmittedClose')"
              :button-confirm-text="$t('designSubmittedConfirm')"
              @onModalClosed="onPromptModalSubmitClosed"
          />
        </div>
      </div>
    </div>
    <Loader ref="loader"/>
  </div>
</template>

<script>
import { nextTick } from "vue";
import SplitButton from "@/components/shared/SplitButton.vue";
import isUUID from "@/utils/helpers/isUUID";
import confetti from "canvas-confetti";
import PromptModal from "@/components/modals/PromptModal.vue";
import Loader from "@/components/shared/Loader.vue";
import { getAssetPath } from "@/utils/assetPath";
import { trackEvent } from "@/utils/tracking";

export default {
  setup() {
    return {
      getAssetPath
    };
  },
  components: {
    PromptModal,
    SplitButton,
    Loader,
  },
  props: {
    enableSave: {
      type: Boolean,
      required: false,
      default: false,
    },
    visible: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      order: {},
      jobs: [],
      variables: [],
      filteredVariables: [],
      selectedJobIndex: null,
      copy: null,
      importing: false,
      assigning: false,
      importURL: "",
      searchTerm: "",
      selectedProfile: null,
      orderLoading: false,
    };
  },
  computed: {
    savedJobs() {
      return this.jobs.filter((j) => !!j.design_ready);
    },
    enableSubmit() {
      return (
          this.savedJobs.length === this.jobs?.length &&
          this.order.status !== "submitted" &&
          this.order.status !== "printed"
      );
    },
  },
  methods: {
    show() {
      this.visible = true;
    },
    assignProfile(profile, job, selectProfileFromDropdown = true) {
      if (this.variables?.length === 0) return;

      if (
          job.variablesReplaced !== false &&
          selectProfileFromDropdown === true &&
          (!this.assigning || (!this.assigning && job.profile))
      ) {
        return (this.assigning = true);
      }

      job.profile = profile;
      job.variablesReplaced = true;

      this.$emit("assignProfile", { profile, job });

      if (!profile) {
        this.$emit("objectModified", { profile, job });
      }
      this.$forceUpdate();

      this.assigning = false;
      this.searchTerm = "";
      this.filteredVariables = this.variables;
    },
    loadVariables({ variables }) {
      this.variables = variables;
      this.filteredVariables = variables;
      this.$emit("showToast", {
        message: this.$t('variablesLoaded') + " (" + variables.length + ")",
        type: "success",
      });
    },
    getProfileForJob(jobID) {
      const job = this.jobs.find((job) => job.id === jobID);
      return job ? job.profile : null;
    },
    getJob(jobID) {
      return this.jobs.find((job) => job.id === jobID);
    },
    filterProfiles() {
      if (!this.searchTerm) {
        this.filteredVariables = this.variables;
      } else {
        this.filteredVariables = this.variables.filter((profile) => {
          return (
              profile.name
                  ?.toLowerCase()
                  .includes(this.searchTerm.toLowerCase()) ||
              profile.email?.toLowerCase().includes(this.searchTerm.toLowerCase())
          );
        });
      }
    },
    changeSelectedProduct(
        job,
        index,
        prompt = true,
        close = true,
        scroll = false
    ) {
      if (this.copy) return;
      if (close && this.selectedJobIndex === index) {
        this.$emit("visible", false);
      }
      this.$emit("onProductClick", { job, index, prompt, scroll });
    },
    async selectedJob(index, scroll = false) {
      this.selectedJobIndex = index;
      if (scroll) {
        setTimeout(() => {
          this.$refs.jobs[this.selectedJobIndex - 1].scrollIntoView({
            behavior: "smooth",
          });
        }, 200);
      }
    },
    selectNextProduct() {
      if (this.selectedJobIndex + 1 < this.jobs?.length) {
        this.selectedJobIndex++;
        const nextJob = this.jobs[this.selectedJobIndex];
        this.changeSelectedProduct(
            nextJob,
            this.selectedJobIndex,
            false,
            false
        );
      }
    },
    updateDesigns(design, type) {
      this.jobs.forEach((job) => {
        if (job.type === type) {
          job.design_preview.front = design;
          job.design_ready = true;
        }
      });
      this.$forceUpdate();
    },
    updateDesign(design, companyID) {
      const job = this.jobs[this.selectedJobIndex];

      job.design_preview.front = design;
      job.design_ready = true;

      this.$forceUpdate();
    },
    saveJobAsTemplate(job) {
      const payload = {
        ...(job.edited_design || job.original_design),
        image: job.design_preview?.front,
        profile: undefined,
      };
      this.$emit("saveTemplate", payload);
    },
    paste(job, index, resetCopy = true) {
      this.$emit("pasteSingle", job.id);
      const sourceJob = this.jobs[this.selectedJobIndex];
      job.design_preview.front = sourceJob.design_preview.front;
      job.design_ready = true;
      this.$forceUpdate();
    },
    extractId(url) {
      let [ baseUrl ] = url.split("?");

      let segments = baseUrl.split("/");

      return segments.pop();
    },
    pasteToAllAtOnce() {
      let design_ids = [];
      this.jobs.forEach((job, index) => {
        if (this.selectedJobIndex === index) return;
        if (!this.isSameVariant(job)) return;

        design_ids.push(job.id);
      });
      this.$emit("pasteToAllAtOnce", design_ids);
      this.copy = null;
    },
    assignToAll() {
      this.variables.forEach((profile, index) => {
        if (this.jobs[index]) {
          this.jobs[index].profile = profile;
          this.jobs[index].variablesReplaced = false;
        }
      });
      this.assigning = false;
    },
    async importDesign() {
      if (!this.importing) return (this.importing = true);

      if (
          this.importURL.includes("designer.solflare.com") ||
          this.importURL.includes("designer-dev.solflare.com")
      ) {
        this.importURL = this.extractId(this.importURL);
      }

      if (!this.importURL || !isUUID(this.importURL)) {
        return this.$emit("showToast", {
          message: this.$t("invalidDesignID"),
          type: "error",
        });
      }

      this.$emit(
          "importDesign",
          this.importURL,
          this.importURL.includes("template") ? "template" : "job"
      );

      this.importing = false;
      this.importURL = "";
    },
    copyDesignLink(job) {
      navigator.clipboard.writeText("https://designer.solflare.com/" + job.id);
      this.$emit("showToast", {
        message: this.$t("linkCopied"),
        type: "success",
      });
    },
    copyDesign() {
      this.copy = this.jobs[this.selectedJobIndex];
    },
    cancelAction() {
      this.copy = null;
      this.importing = false;
      this.assigning = false;
    },
    isSameVariant(job) {
      const jobVariant = job.variant;
      const selectedJobVariant = this.jobs[this.selectedJobIndex].variant;
      const isSameVariant =
          jobVariant === selectedJobVariant ||
          (jobVariant &&
              jobVariant.indexOf("metal") !== -1 &&
              selectedJobVariant &&
              selectedJobVariant.indexOf("metal") !== -1) ||
          (jobVariant &&
              jobVariant.indexOf("engraving") !== -1 &&
              selectedJobVariant &&
              selectedJobVariant.indexOf("engraving") !== -1);

      return isSameVariant;
    },
    init() {
      this.orderLoading = true;
      fetch(
          `${import.meta.env.VITE_APP_API_ROOT}/order/jobs/${this.orderId}`
      ).then(async (response) => {
        const data = await response.json();

        if (data.jobs) {
          data.jobs.forEach((job, index) => {
            if (job.edited_design?.profile) {
              job.profile = job.edited_design.profile;
            }
          });

          this.jobs = data.jobs;
          this.order = data.order;

          // Check order status and show loader if submitted/printed
          if (
              this.order.status === "submitted" ||
              this.order.status === "printed"
          ) {
            // Show the loader message
            this.$refs.loader.show(
                this.$t("designSubmitted") +
                "<br><br>" +
                this.$t("designSubmittedP"),
                true
            );

            setTimeout(() => {
              this.$refs.loader.hide(true);
            }, 5000);

            this.$emit("orderSubmitted");
          }
        }

        await nextTick();
        this.orderLoading = false;

        setTimeout(() => {
          if (this.jobs?.length) {
            this.changeSelectedProduct(this.jobs[0], 0, false);
          }
        }, 500);

        this.$emit("afterLoad", this.jobs);
      });
    },
    async submitDesign() {
      if (!this.enableSubmit) return;

      return this.$refs.promptModalSubmit.open();
    },
    async onPromptModalSubmitClosed({ success, state }) {
      if (success) {
        let response = await fetch(
            `${import.meta.env.VITE_APP_API_ROOT}/order/${
                this.order.hash
            }/submit`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json"
              },
              body: JSON.stringify({}),
            }
        );
        response = await response.json();
        if (response?.error?.errorKey || response.success === false) {
          return this.$emit("showToast", {
            message: response?.error?.errorKey
                ? this.$t(response?.error?.errorKey)
                : this.$t("saveJobFailedMessage"),
            type: "error",
          });
        } else {
          this.order = response.order;
          trackEvent({
            eventName: "Customizer",
            eventType: "Submit",
            action: "OrderSubmitted",
            details: this.order?.hash ? { orderId: this.order.hash } : undefined,
          });
          this.$emit("showToast", {
            message: this.$t("designSubmitted"),
            type: "success",
          });
          this.triggerConfetti();
        }
      }
    },
    triggerConfetti() {
      let count = 700;
      let defaults = {
        origin: { y: 0.7 },
        gravity: 0.7,
      };

      function fire(particleRatio, opts) {
        confetti({
          ...defaults,
          ...opts,
          particleCount: Math.floor(count * particleRatio),
        });
      }

      fire(0.25, {
        spread: 26,
        startVelocity: 55,
      });
      fire(0.2, {
        spread: 60,
      });
      fire(0.35, {
        spread: 100,
        decay: 0.91,
        scalar: 0.8,
      });
      fire(0.1, {
        spread: 120,
        startVelocity: 25,
        decay: 0.92,
        scalar: 1.2,
      });
      fire(0.1, {
        spread: 120,
        startVelocity: 45,
      });
    },
  },
  watch: {
    visible() {
      this.$forceUpdate();
    },
    "order.status"(nv) {
      if (nv === "submitted") this.$emit("orderSubmitted");
    },
  },
  mounted() {
    this.orderId = location.pathname.split("/").pop();
    this.init();
  },
};
</script>

<style lang="scss" scoped>
#cardDesigner {
  .productsContainer {
    border-right: 1px solid var(--color-border-light-20);
  }

  .progress-container {
    padding: 10px;
    width: 100%;

    button, div[class*="submitDesign"] {
      border: 2px solid var(--color-sol-yellow) !important;
    }
  }

  .progress-label {
    text-align: left;
    font-size: 16px;
    margin-bottom: 8px;
    color: var(--color-white);
  }

  .progress-bar {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 5px 0;
  }

  .progress-segment {
    cursor: pointer;
    flex-grow: 1;
    height: 4px;
    border-radius: 2px;
    background-color: var(--color-bg-gray-muted);
    margin: 0 2px;
    transition: background-color 0.3s;
  }

  .done {
    background-color: var(--color-success) !important;
  }

  .selected {
    color: var(--color-sol-yellow);
  }

  .in-progress {
    color: var(--color-sol-yellow);
  }

  .printed {
    color: var(--color-sol-yellow);
  }

  .badge {
    display: inline-block;
    padding: 5px 10px;
    color: black;
    border-radius: 10px;
    font-weight: bold;
  }

  .sticker-img {
    height: 200px;
    object-fit: cover;
  }

  canvas {
    z-index: 9999999999999999999999999 !important;
  }
}
</style>
